import type { StockSeries } from '@jums-dashboard/shared';
import {
  BarController,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Tooltip as ChartTooltip,
  Filler,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
} from 'chart.js';
import { useMemo } from 'react';
import { Chart } from 'react-chartjs-2';

import Modal from '@/components/Modal/Modal';
import WidgetDetailChrome from '@/features/dashboard/components/WidgetDetailChrome';
import { cn } from '@/lib/cn';
import { formatRelativeTime } from '@/lib/formatRelativeTime';

import type { ResolvedAsset } from './config';
import { CHANGE_COLOR_CLASS, changeSign, deriveAsset, formatDayChange, formatPrice, type UpDown } from './stocksDerive';

ChartJS.register(BarController, BarElement, CategoryScale, ChartTooltip, Filler, LinearScale, LineController, LineElement, PointElement);

interface StocksDetailModalProps {
  open: boolean;
  onClose: () => void;
  assets: ResolvedAsset[];
  seriesById: Record<string, StockSeries>;
  selectedId: string | null;
  onSelect: (assetId: string) => void;
  lastUpdated?: Date | null;
  onRefresh?: () => void;
}

function fmtDateTime(seconds: number): string {
  return new Date(seconds * 1000).toLocaleString(undefined, { weekday: 'short', hour: '2-digit', minute: '2-digit' });
}

export default function StocksDetailModal({
  open,
  onClose,
  assets,
  seriesById,
  selectedId,
  onSelect,
  lastUpdated,
  onRefresh,
}: StocksDetailModalProps) {
  const asset = assets.find((a) => a.id === selectedId) ?? assets[0] ?? null;
  const series = asset ? seriesById[asset.id] : undefined;
  const derived = deriveAsset(series);
  const currency = asset?.currency || series?.currency || '';
  const sign = changeSign(derived.dayChangeAbs);

  if (!open || !asset) return null;

  const updatedAt = series?.regularMarketTime ? new Date(series.regularMarketTime * 1000) : null;

  return (
    <Modal open={open} onClose={onClose} title={`Stocks · ${asset.label}`} size="lg">
      <WidgetDetailChrome widgetId="stocks" lastUpdated={lastUpdated} onRefresh={onRefresh} />
      {/* Asset selector pills */}
      {assets.length > 1 && (
        <div className="mb-4 flex flex-wrap gap-2" role="group" aria-label="Choose asset">
          {assets.map((a) => {
            const active = a.id === asset.id;
            return (
              <button
                key={a.id}
                type="button"
                aria-pressed={active}
                onClick={() => onSelect(a.id)}
                className={cn(
                  'rounded-full border px-3 py-1 text-xs font-medium transition-colors',
                  'focus-visible:ring-2 focus-visible:ring-(--accent-primary) focus-visible:ring-offset-1 focus-visible:ring-offset-(--bg-surface-strong) focus-visible:outline-none',
                  active
                    ? 'border-(--accent-primary-strong) bg-(--accent-primary-strong) text-(--text-on-accent)'
                    : 'border-(--border-default) bg-(--bg-surface) text-(--text-secondary) hover:bg-(--bg-surface-hover)',
                )}
              >
                {a.label}
              </button>
            );
          })}
        </div>
      )}

      {/* Header */}
      <div className="mb-4 flex flex-wrap items-baseline gap-x-4 gap-y-1">
        <span className="font-display text-4xl font-semibold text-(--text-primary) tabular-nums">
          {formatPrice(derived.current, currency)}
        </span>
        <span className={cn('text-lg tabular-nums', CHANGE_COLOR_CLASS[sign])}>
          {formatDayChange(derived.dayChangeAbs, derived.dayChangePct)}
        </span>
        {updatedAt && <span className="ml-auto text-xs text-(--text-muted)">Updated {formatRelativeTime(updatedAt)}</span>}
      </div>

      {/* Chart / states */}
      {series?.error ? (
        <div className="rounded-lg border border-(--border-subtle) bg-(--bg-surface) p-6 text-sm text-(--accent-danger)">
          {asset.label} is unavailable right now ({series.error}).
        </div>
      ) : !derived.hasData ? (
        <div className="rounded-lg border border-(--border-subtle) bg-(--bg-surface) p-6 text-sm text-(--text-tertiary)">
          No recent data for {asset.label}.
        </div>
      ) : (
        <StocksChart
          series={series!}
          todayStartIndex={derived.todayStartIndex}
          upDown={derived.upDown}
          hasVolume={derived.hasVolume && asset.hasVolume}
          sign={sign}
        />
      )}

      {asset.nordnetUrl && (
        <div className="mt-4">
          <a
            href={asset.nordnetUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-(--accent-primary) hover:underline focus-visible:ring-2 focus-visible:ring-(--accent-primary) focus-visible:outline-none"
          >
            Open in Nordnet ↗
          </a>
        </div>
      )}
    </Modal>
  );
}

// ─── Chart ───────────────────────────────────────────────────────────────

interface StocksChartProps {
  series: StockSeries;
  todayStartIndex: number;
  upDown: UpDown[];
  hasVolume: boolean;
  sign: UpDown;
}

function StocksChart({ series, todayStartIndex, upDown, hasVolume, sign }: StocksChartProps) {
  const colors = useMemo(() => {
    const style = getComputedStyle(document.documentElement);
    const read = (name: string, fallback: string) => style.getPropertyValue(name).trim() || fallback;
    return {
      up: read('--accent-success', '#22c55e'),
      down: read('--accent-danger', '#ef4444'),
      muted: read('--text-muted', '#94a3b8'),
      focus: read('--border-focus', '#ffffff'),
    };
  }, []);

  const priceColor = sign === 'down' ? colors.down : sign === 'up' ? colors.up : colors.muted;
  const points = series.points;

  const chartData = useMemo(() => {
    const datasets: Record<string, unknown>[] = [
      {
        type: 'line' as const,
        label: 'Price',
        data: points.map((p) => p.close),
        borderColor: priceColor,
        backgroundColor: `${priceColor}22`,
        borderWidth: 2,
        pointRadius: 0,
        tension: 0.3,
        fill: true,
        yAxisID: 'yPrice',
        order: 1,
      },
    ];
    if (hasVolume) {
      datasets.push({
        type: 'bar' as const,
        label: 'Volume',
        data: points.map((p) => p.volume ?? 0),
        backgroundColor: upDown.map((d) => `${d === 'down' ? colors.down : colors.up}55`),
        borderWidth: 0,
        yAxisID: 'yVolume',
        order: 2,
      });
    }
    return { labels: points.map((_, i) => i), datasets };
  }, [points, priceColor, hasVolume, upDown, colors.up, colors.down]);

  const todayMarkerPlugin = useMemo(
    () => ({
      id: 'stocksTodayMarker',
      beforeDatasetsDraw(chart: ChartJS) {
        if (todayStartIndex <= 0) return;
        const { ctx, chartArea, scales } = chart;
        const xScale = scales.x;
        if (!xScale || !chartArea) return;
        const x = xScale.getPixelForValue(todayStartIndex);
        if (!Number.isFinite(x)) return;
        ctx.save();
        ctx.strokeStyle = `${colors.focus}99`;
        ctx.lineWidth = 1;
        ctx.setLineDash([4, 3]);
        ctx.beginPath();
        ctx.moveTo(x + 0.5, chartArea.top);
        ctx.lineTo(x + 0.5, chartArea.bottom);
        ctx.stroke();
        ctx.setLineDash([]);
        ctx.fillStyle = colors.muted;
        ctx.font = '10px system-ui, sans-serif';
        ctx.textAlign = 'left';
        ctx.fillText('today', x + 4, chartArea.top + 10);
        ctx.restore();
      },
    }),
    [todayStartIndex, colors.focus, colors.muted],
  );

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      animation: false as const,
      interaction: { mode: 'index' as const, intersect: false },
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            title: (items: { dataIndex: number }[]) => {
              const p = points[items[0]?.dataIndex ?? 0];
              return p ? fmtDateTime(p.t) : '';
            },
            label: (item: { dataset: { label?: string }; parsed: { y: number | null } }) => {
              const value = item.parsed.y;
              if (value == null) return '';
              if (item.dataset.label === 'Volume') return `Volume: ${Math.round(value).toLocaleString()}`;
              return `Price: ${value.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
            },
          },
        },
      },
      scales: {
        x: {
          grid: { display: false },
          border: { display: false },
          ticks: {
            color: colors.muted,
            font: { size: 10 },
            maxRotation: 0,
            autoSkip: true,
            maxTicksLimit: 6,
            callback: (_v: string | number, i: number) => {
              const p = points[i];
              return p ? fmtDateTime(p.t) : '';
            },
          },
        },
        yPrice: {
          position: 'left' as const,
          grid: { color: `${colors.muted}22`, drawTicks: false },
          border: { display: false },
          ticks: { color: colors.muted, font: { size: 10 }, padding: 6 },
        },
        yVolume: {
          display: hasVolume,
          position: 'right' as const,
          grid: { display: false },
          border: { display: false },
          ticks: { display: false },
          beginAtZero: true,
          // Keep volume bars in the lower ~third so they don't dominate the price area.
          suggestedMax: Math.max(1, ...points.map((p) => p.volume ?? 0)) * 3,
        },
      },
    }),
    [points, colors, hasVolume],
  );

  return (
    <div className="h-72">
      {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
      <Chart type="bar" data={chartData as any} options={options as any} plugins={[todayMarkerPlugin] as any} />
    </div>
  );
}
